import React, { createContext, useContext, useEffect, useState } from 'react'
import { fetchDraftBoardPlayers, fetchDraftPicksByYear } from '../../Api'
import { DraftPick, DraftPlayer } from '../../Api/Draft/draft.types'
import { DRAFT_YEAR, prevDraftYearsList } from '../../Config/League/league.config'

type DraftContextProps = {
  currentYearDraftPicks: DraftPick[]
  prevDraftPicks: { [key: number]: DraftPick[] } | null
  availablePlayers: DraftPlayer[]
  isLoading: boolean
  isLoadingPreviousDraftPicks: boolean
  fetchDraftPicks: (year: number) => Promise<DraftPick[]>
}

const DraftContext = createContext<DraftContextProps | null>(null)
export const useDraftContext = () => useContext(DraftContext)!

export const DraftProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [currentYearDraftPicks, setCurrentYearDraftPicks] = useState<DraftPick[]>([])
  const [prevDraftPicks, setPrevDraftPicks] = useState<{
    [key: number]: DraftPick[]
  } | null>(null)
  const [availablePlayers, setAvailablePlayers] = useState<DraftPlayer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingPreviousDraftPicks, setIsLoadingPreviousDraftPicks] =
    useState<boolean>(true)

  useEffect(() => {
    fetchDraftData()
  }, [])

  const fetchDraftData = async () => {
    await Promise.all([
      fetchDraftPicks(DRAFT_YEAR),
      fetchPlayersOnDraftBoard(DRAFT_YEAR),
    ])
    setIsLoading(false)
    fetchPreviousDraftPicks()
  }

  const fetchDraftPicks = async (year: number) => {
    const picks = await fetchDraftPicksByYear(year)
    setIsLoading(false)
    if (!picks || picks.length < 1) {
      return []
    }

      setCurrentYearDraftPicks(picks)
    return picks
  }

  const fetchPlayersOnDraftBoard = async (year: number) => {
    const players = await fetchDraftBoardPlayers(year)
    if (!players || players.length < 1) {
      setIsLoading(false)
      return
    }
    setAvailablePlayers(players.sort((a, b) => a.overallRank - b.overallRank))
  }

  const fetchPreviousDraftPicks = async () => {
    let previousPicks = { ...prevDraftPicks }
    await Promise.all(
      prevDraftYearsList.map(async (year) => {
        const picks = await fetchDraftPicksByYear(year)
        if (!picks || picks.length < 1) {
          previousPicks[year] = []
          return
        }
        previousPicks[year] = picks
        return
      })
    )
    setPrevDraftPicks(previousPicks)
    setIsLoadingPreviousDraftPicks(false)
  }

  return (
    <DraftContext.Provider
      value={{
        currentYearDraftPicks,
        prevDraftPicks,
        availablePlayers,
        isLoading,
        isLoadingPreviousDraftPicks,
        fetchDraftPicks,
      }}
    >
      {children}
    </DraftContext.Provider>
  )
}
